import React, { RefObject, useState } from 'react';
import toast from 'react-hot-toast';
import { EditorState, RichUtils } from 'draft-js';
import {
    BiBold,
    BiFontColor,
    BiFontSize,
    BiItalic,
    BiLink,
    BiListUl,
    BiStrikethrough,
    BiUnderline,
    BiUnlink,
} from 'react-icons/bi';
import { useI18nContext } from '@i18n/i18n-react';
import { Upload } from '@components/icons/FarseerIcons';
import { TextInput } from '@components/form';
import { useFilePicker } from '@hooks/useFilePicker';

import {
    changeColorStyle,
    changeFontStyle,
    insertImage,
    applyLink,
    hasLinkInSelection,
    isLinkableSelection,
    CustomTextColorStyle,
    CustomTextSizeStyle,
} from './editorStyles';
import { isSafeLinkUrl } from './linkPlugin';
import { appColors } from '../../../base/useTheme';
import { ContextMenu, VirtualElement } from '@components/context-menu/ContextMenu';

function ColorBox({ color }: { color: string }) {
    return (
        <div
            style={{
                minHeight: '2em',
                maxHeight: '2em',
                minWidth: '2em',
                maxWidth: '2em',
                backgroundColor: color,
                cursor: 'pointer',
                borderRadius: '5px',
                margin: '0 2px',
            }}
        />
    );
}

interface TextEditMenuProps {
    open: boolean;
    setOpen: (b: boolean) => void;
    anchorRef: RefObject<HTMLDivElement | VirtualElement>;

    editorState: EditorState;
    updateEditorState: (newEditorState: EditorState) => void;
}

export type Uploaded = { name: string; src: string };
export type FileData = { files: File[] };

export const handleUpload = (
    data: FileData,
    success: (uploads: Uploaded[]) => void,
    error: (upload: Uploaded) => void
) => {
    const [upload] = data.files;
    if (!upload) return '';

    const reader = new FileReader();
    reader.addEventListener('load', () => {
        if (typeof reader.result === 'string') {
            const { name, size, type } = upload;
            const file = { name, src: reader.result };

            if (size < 1024 * 1024 && type.match(/^image\//)) {
                success([file]);
            } else {
                error(file);
            }
        }
    });
    reader.readAsDataURL(upload);
    return '';
};

export const TextEditMenu = ({ open, setOpen, anchorRef, editorState, updateEditorState }: TextEditMenuProps) => {
    const { LL } = useI18nContext();

    const handleClick = (action: 'BOLD' | 'ITALIC' | 'UNDERLINE' | 'STRIKETHROUGH' | 'LIST') => {
        const newState =
            action === 'LIST'
                ? RichUtils.toggleBlockType(editorState, 'unordered-list-item')
                : RichUtils.toggleInlineStyle(editorState, action);
        if (newState !== null && newState !== editorState) {
            updateEditorState(newState);
        }
    };

    const handleFontChange = (sizeStyle: CustomTextSizeStyle) => {
        const newState = changeFontStyle(editorState, sizeStyle);
        if (newState !== editorState) {
            updateEditorState(newState);
        }
    };

    const handleColorChange = (colorStyle: CustomTextColorStyle) => {
        const newState = changeColorStyle(editorState, colorStyle);
        if (newState !== editorState) {
            updateEditorState(newState);
        }
    };

    const handleImage = (upload: File | null) => {
        if (!upload) return;

        handleUpload(
            { files: [upload] },
            (uploads: Uploaded[]) => {
                const [upload] = uploads;

                const newState = insertImage(editorState, upload.src);
                if (newState !== editorState) {
                    updateEditorState(newState);
                }
            },
            () => {
                toast.error(LL.FILE_SIZE_1MB());
            }
        );
    };

    const [linkUrl, setLinkUrl] = useState('');

    const handleLink = (closeMenu: () => void) => {
        const trimmedUrl = linkUrl.trim();
        if (!trimmedUrl) return;

        // In-app URLs become hash router links, so they open in the same tab
        const appUrl = window.location.origin + window.location.pathname;
        const url = trimmedUrl.startsWith(`${appUrl}#/`) ? trimmedUrl.slice(appUrl.length) : trimmedUrl;
        if (!isSafeLinkUrl(url)) {
            toast.error(LL.LINK_URL_INVALID());
            return;
        }

        updateEditorState(applyLink(editorState, url));
        setLinkUrl('');
        closeMenu();
    };

    const handleUnlink = () => {
        updateEditorState(RichUtils.toggleLink(editorState, editorState.getSelection(), null));
    };

    const { openFilePicker } = useFilePicker();

    return (
        <ContextMenu open={open} setOpen={setOpen} anchorRef={anchorRef} direction="horizontal" noFocus>
            <ContextMenu.Item item={{ icon: () => <BiBold />, action: () => handleClick('BOLD') }} />
            <ContextMenu.Item item={{ icon: () => <BiItalic />, action: () => handleClick('ITALIC') }} />
            <ContextMenu.Item item={{ icon: () => <BiUnderline />, action: () => handleClick('UNDERLINE') }} />
            <ContextMenu.Item item={{ icon: () => <BiStrikethrough />, action: () => handleClick('STRIKETHROUGH') }} />
            <ContextMenu.Divider />
            <ContextMenu.Item item={{ icon: () => <BiFontSize /> }}>
                <ContextMenu.Item
                    item={{
                        label: '10',
                        action: () => handleFontChange(CustomTextSizeStyle.SIZE_10),
                    }}
                />
                <ContextMenu.Item
                    item={{
                        label: '12',
                        action: () => handleFontChange(CustomTextSizeStyle.SIZE_12),
                    }}
                />
                <ContextMenu.Item
                    item={{
                        label: '14',
                        action: () => handleFontChange(CustomTextSizeStyle.SIZE_14),
                    }}
                />
                <ContextMenu.Item
                    item={{
                        label: '18',
                        action: () => handleFontChange(CustomTextSizeStyle.SIZE_18),
                    }}
                />
            </ContextMenu.Item>
            <ContextMenu.Divider />
            <ContextMenu.Item item={{ icon: () => <BiFontColor /> }} direction="horizontal">
                <ContextMenu.Item
                    item={{
                        render: () => <ColorBox color={appColors.ui.dark} />,
                        action: () => handleColorChange(CustomTextColorStyle.COLOR_UI),
                    }}
                />
                <ContextMenu.Item
                    item={{
                        render: () => <ColorBox color={appColors.primary.w500} />,
                        action: () => handleColorChange(CustomTextColorStyle.COLOR_PRIMARY),
                    }}
                />
                <ContextMenu.Item
                    item={{
                        render: () => <ColorBox color={appColors.error.w500} />,
                        action: () => handleColorChange(CustomTextColorStyle.COLOR_ERROR),
                    }}
                />
                <ContextMenu.Item
                    item={{
                        render: () => <ColorBox color={appColors.neutral.w700} />,
                        action: () => handleColorChange(CustomTextColorStyle.COLOR_NEUTRAL),
                    }}
                />
            </ContextMenu.Item>
            <ContextMenu.Divider />
            <ContextMenu.Item item={{ icon: () => <BiListUl />, action: () => handleClick('LIST') }} />
            <ContextMenu.Divider />
            <ContextMenu.Item item={{ icon: () => <BiLink />, disabled: !isLinkableSelection(editorState) }}>
                <ContextMenu.Item
                    item={{
                        renderOutside: ({ closeMenu }) => (
                            <TextInput
                                autoFocus
                                width={240}
                                value={linkUrl}
                                onChange={setLinkUrl}
                                placeholder={LL.LINK_URL_PLACEHOLDER()}
                                onKeyDown={(e) => {
                                    if (e.key !== 'Enter') return;

                                    e.preventDefault();
                                    handleLink(closeMenu);
                                }}
                            />
                        ),
                    }}
                />
            </ContextMenu.Item>
            <ContextMenu.Item
                item={{ icon: () => <BiUnlink />, action: handleUnlink, disabled: !hasLinkInSelection(editorState) }}
            />
            <ContextMenu.Divider />
            <ContextMenu.Item item={{ icon: () => <Upload />, action: () => openFilePicker(handleImage) }} />
        </ContextMenu>
    );
};
